import type { ProductType, Prompt } from "@1js/pl-card-components";
import { isExtensionPrompt, isWorkgroupPrompt } from "@1js/pl-utils";
import { useMsal } from "@azure/msal-react";
import * as React from "react";
import UAParser from "ua-parser-js";
import { MetaOSAppContext } from "../pages/metaos/MetaOSAppContext";
import { LibraryContext } from "../pages/prompts/allPromptsLibrary/LibraryContext";
import { PropertyContext } from "../pages/PropertyContext.web";
import { handoffToAugloop } from "../services/handoffAugloop";
import { handoffToM365Chat } from "../services/handoffToM365Chat";
import type { HandoffFile } from "../services/workingsetfiles-client.web";
import { type SiteSetting } from "../types";
import useTelemetry from "../utils/Telemetry/useTelemetry";
import {
  type HandoffCallback,
  type HandoffCallbackResult,
  type HandoffResult,
} from "./handoff.types";
import { useFeatureEnablement } from "./useFeatureEnablement";
import { useFlightContext } from "./useFlightContext";

type ProductMappingType = Map<
  ProductType | string,
  {
    requirements: (settings: SiteSetting, prompt?: Prompt) => boolean;
    method: (
      prompt: Prompt,
      product: ProductType | string,
      host: string,
      locale: string,
      file: HandoffFile | undefined,
      abortController: AbortController
    ) => Promise<HandoffCallbackResult>;
  }
>;

/**
 * Orchestrates handoff of prompts to different products and services,
 * like M365 Chat and Office AI (Word, Excel, PowerPoint, OneNote, Outlook),
 *  to enable customers to try prompts in those products and services.
 * @returns methods to handoff prompts to different products and services
 */
export const useHandoffService = (): HandoffResult => {
  const [siteSetting] = React.useContext(PropertyContext);
  const { state } = React.useContext(LibraryContext);
  const { isFlightActive } = useFlightContext();
  const { device } = new UAParser().getResult();
  const { includesAny } = useFeatureEnablement();
  const telemetry = useTelemetry();
  const metaOSAppContext = React.useContext(MetaOSAppContext);
  const msalInstance = useMsal();
  const instance =
    metaOSAppContext.isMosApp && metaOSAppContext.pca
      ? msalInstance.instance
      : undefined;

  const isBusinessChatLicenseActive = includesAny(siteSetting.licenses, [
    "CopilotBusinessChat",
    "Copilot",
  ]);

  const isCWCEligible = state?.isUserEligibleForCompliantWebChat ?? false;
  const isMobile = React.useCallback(() => {
    return (
      device.type === "mobile" ||
      device.type === "tablet" ||
      device.type === "wearable"
    );
  }, [device.type]);

  const productsInFlight = React.useMemo(() => {
    const products = [];
    if (!isMobile()) {
      if (isFlightActive("isTryInWordEnabled")) {
        products.push("Word");
      }

      if (isFlightActive("isTryInExcelEnabled")) {
        products.push("Excel");
      }

      if (isFlightActive("isTryInPowerPointEnabled")) {
        products.push("PowerPoint");
      }
    }

    return products;
  }, [isFlightActive, isMobile]);

  const productMapping: ProductMappingType = React.useMemo(() => {
    const innerMap: ProductMappingType = new Map();
    // Returns true if prompt is not workgroup prompt or if it indeed is a workgroup prompt and flag is enabled
    const workgroupPromptConditional = (prompt: Prompt | undefined) =>
      !isWorkgroupPrompt(prompt) ||
      (isFlightActive("isTryInTeamPromptsEnabled") &&
        isWorkgroupPrompt(prompt));

    // Conditions for M365 Chat handoff (e.g. Bizchat, CWC)
    const m365ChatRequirements = (prompt?: Prompt) => {
      return (
        (((isFlightActive("isTryInDisableCosmosDBEnabled") &&
          prompt?.Repository !== "CosmosDB") ||
          !isFlightActive("isTryInDisableCosmosDBEnabled")) &&
          !isMobile() &&
          (!isFlightActive("isTryIn1_1FormatEnabled") ||
            (isFlightActive("isTryIn1_1FormatEnabled") &&
              prompt?.Origin !== "User" &&
              !isWorkgroupPrompt(prompt)) ||
            (isFlightActive("isTryIn3SPromptLinkEnabled") &&
              workgroupPromptConditional(prompt)) ||
            (isFlightActive("isTryInBizChatEnabledForAgents") &&
              isExtensionPrompt(prompt as Prompt)))) ||
        false
      );
    };

    innerMap.set("BizChat", {
      requirements: (settings: SiteSetting, prompt?: Prompt) =>
        isFlightActive("isTryInBizChatEnabled") &&
        m365ChatRequirements(prompt) &&
        includesAny(settings.licenses, ["CopilotBusinessChat", "Copilot"]),
      method: async (prompt: Prompt) =>
        await handoffToM365Chat(
          prompt,
          "M365Chat",
          isFlightActive("isTryInAppendM365ChatFeatures"),
          isFlightActive("isTryIn1_1FormatEnabled"),
          isFlightActive("isTryIn3SPromptLinkEnabled"),
          telemetry.sendApiActionEvent,
          instance,
          "work",
          isFlightActive("isBizChatTryInTeamsAppEnabled"),
          undefined,
          isFlightActive("isTryInBizChatEnabledForAgents")
        ),
    });
    innerMap.set("CWC", {
      requirements: (settings: SiteSetting, prompt?: Prompt) =>
        m365ChatRequirements(prompt) &&
        ((isFlightActive("isTryInCWCForBusinessChatUsersEnabled") &&
          includesAny(settings.licenses, ["CopilotBusinessChat", "Copilot"])) ||
          (isFlightActive("isTryInCWCForNonAIUsersEnabled") && isCWCEligible)),
      method: async (prompt: Prompt) =>
        //Always route to web tab for CWC
        await handoffToM365Chat(
          prompt,
          "M365Chat",
          isFlightActive("isTryInAppendM365ChatFeatures"),
          isFlightActive("isTryIn1_1FormatEnabled"),
          isFlightActive("isTryIn3SPromptLinkEnabled"),
          telemetry.sendApiActionEvent,
          instance,
          "web",
          undefined,
          undefined,
          isFlightActive("isTryInBizChatEnabledForAgents")
        ),
    });

    productsInFlight.forEach((product) => {
      innerMap.set(product, {
        requirements: (settings: SiteSetting, prompt?: Prompt) =>
          // AAD licenses
          ((((includesAny(settings.licenses, ["OfficeProPlus"]) ||
            (isFlightActive("isTryInOfficeWebLicenseEnabled") &&
              includesAny(settings.licenses, ["OfficeWeb"]))) &&
            includesAny(settings.licenses, ["CopilotAI"])) ||
            // MSA licenses
            (isFlightActive("isTryInOfficeMsaEnabled") &&
              includesAny(settings.licenses, ["Family", "Personal"]) &&
              includesAny(settings.licenses, ["CopilotInM365"])) ||
            includesAny(settings.licenses, ["Copilot"])) &&
            isFlightActive("isTryInOfficeEnabled") &&
            workgroupPromptConditional(prompt)) ||
          false,
        method: async (
          prompt: Prompt,
          product: string,
          host: string,
          locale: string,
          file: HandoffFile | undefined,
          abortController: AbortController
        ) => {
          const useNarrativeBuilder =
            isFlightActive("isNarrativeBuilderEnabled") &&
            product === "PowerPoint" &&
            promptHasMetadata(prompt, "NewDocExperience") &&
            !file;

          return await handoffToAugloop(
            host,
            locale,
            prompt,
            product,
            telemetry.sendApiActionEvent,
            file,
            abortController,
            instance,
            siteSetting.user?.email,
            isFlightActive("isAgentPromptHandoffToAugloopEnabled"),
            useNarrativeBuilder
          );
        },
      });
    });
    return innerMap;
  }, [
    includesAny,
    instance,
    isFlightActive,
    isMobile,
    productsInFlight,
    siteSetting.user?.email,
    telemetry.sendApiActionEvent,
    isCWCEligible,
  ]);

  /**
   * Method to handoff the prompt to the appropriate product or service
   * @param props.prompt - The prompt to handoff
   * @param props.destination - The destination product or service to handoff the prompt to
   * @param props.host - The host of SupportService
   * @param props.locale - The locale of the user
   * @returns handoffUrl to direct customer to in browser, or error if handoff failed
   */
  const onHandoff = async (
    props: HandoffCallback
  ): Promise<HandoffCallbackResult> => {
    const { prompt, destination, host, locale, file, abortController } = props;
    if (!productMapping.has(destination)) {
      throw new Error("UnknownDestination");
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const handoffMethod = productMapping.get(destination)!;

    return await handoffMethod.method(
      prompt,
      destination,
      host,
      locale,
      file,
      abortController
    );
  };

  const promptHasMetadata = (prompt: Prompt, metadata: string): boolean => {
    let result = false;

    // 3S should be providing this metadata but we have been blocked on getting this support
    // Hard coding as a temporary measure to get this feature unblocked
    const BlockTryInWebHardcodedPrompts = [
      "6238c5ed-ae86-464f-9dbc-00c953ebfe9c",
      "090769fb-a31b-499f-8cc9-f1f8492f9bfc",
      "e7e8a3a3-440f-4e87-9aa2-f585a4f098eb",
      "707e629a-78b0-41ef-a9de-7974967fd4ba",
      "87dbd581-5dab-42d7-b93a-7dba5ea5dbce",
      "95c79eb7-5326-4af3-a742-d5c9a033c4cc",
    ];

    const NewDocExperienceHardCodedPrompts = [
      "01db44c0-5c86-461d-b20a-38e1ca54e4a5",
      "893e8202-5574-4534-9d94-4e6f3bb0899c",
      "816ec363-9130-4aee-99b1-822dbc0e87cf",
      "b7b6aa1c-e0a3-4b70-8006-9e73b9fc226b",
      "cf824716-226a-4a3a-a9da-d8450aaf6b0a",
      "13008b3a-11ab-4230-ac43-faa69948d047",
      "a58f5af2-5c43-470a-81d3-90fb8dd2b00e",
      "1a7f2028-52f1-4fb0-9403-c286a8810c48",
    ];

    if (prompt) {
      if (
        metadata === "BlockTryInWeb" &&
        BlockTryInWebHardcodedPrompts.includes(prompt.PromptId)
      )
        return true;
      if (
        metadata === "NewDocExperience" &&
        NewDocExperienceHardCodedPrompts.includes(prompt.PromptId)
      )
        return true;

      prompt.Metadata?.forEach((x) => {
        if (x.Name === metadata && x.Value === "true") {
          result = true;
        }
      });
    }
    return result;
  };

  const isHandoffSupported = React.useCallback(
    (product: ProductType | string, prompt?: Prompt): boolean => {
      if (promptHasMetadata(prompt as Prompt, "BlockTryInWeb")) {
        return false;
      }

      if (!productMapping.has(product)) {
        return false;
      }
      if (!productMapping.get(product)?.requirements(siteSetting, prompt)) {
        return false;
      }
      if (!isExtensionPrompt(prompt as Prompt)) {
        return true;
      }
      // Check product-specific conditions
      if (
        isFlightActive("isTryInWordEnabledForAgents") &&
        (product as ProductType) === ("Word" as ProductType)
      ) {
        return true;
      }
      if (
        isFlightActive("isTryInPowerPointEnabledForAgents") &&
        (product as ProductType) === ("PowerPoint" as ProductType)
      ) {
        return true;
      }
      if (
        isFlightActive("isTryInBizChatEnabledForAgents") &&
        (product as ProductType) == ("BizChat" as ProductType)
      ) {
        return true;
      }
      return false;
    },
    [isFlightActive, productMapping, siteSetting]
  );

  const isProductNeedsFile = (
    product: ProductType | string,
    prompt: Prompt
  ) => {
    if (!productMapping.has(product)) {
      return false;
    }

    // These prompts don't allow selecting existing document
    if (promptHasMetadata(prompt, "NewDocExperience")) {
      return false;
    }

    return !["BizChat", "Teams", "Topics", "CWC"].includes(product);
  };

  const chooseBestProductForHandoff = (
    prompt: Prompt
  ): ProductType | string | undefined => {
    const product = prompt.Products.find((product) =>
      isHandoffSupported(product, prompt)
    );

    //
    //            | Bizchat           | CWC              | Bizchat + CWC     |
    //            |----------         |----------        |----------         |
    // AI Sku     | Work/BizChatLabel | Web/BizChatLabel | Work/BizChatLabel |
    // Non-AI Sku | No Try-in button  | Web/CWCLabel     | Web/CWCLabel      |
    // product contains the first handoff supported product, choose BizChat or CWC based on user license.
    if (product === "CWC" && isBusinessChatLicenseActive) {
      return prompt.Products.includes("BizChat")
        ? "BizChat"
        : "CWCWithBizChatLabel";
    }
    return product;
  };

  return {
    handoff: onHandoff,
    isHandoffSupported,
    isProductNeedsFile,
    chooseBestProductForHandoff,
  };
};
